import * as React from "react";
import type { TimelineEntry, TimelineEntryKind } from "@aos/shared";

import { cn } from "@/lib/utils";
import { StatusBadge, StatusDot } from "@/components/app/StatusBadge";

const KIND_LABEL: Record<TimelineEntryKind, string> = {
  event: "Event",
  rule_run: "Rule Run",
  agent_run: "Agent Run",
  decision: "Decision",
};

function formatAt(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return at;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

export interface TimelineProps {
  /** Chronologically ordered entries (the API returns ascending). */
  items: TimelineEntry[] | undefined;
  /** Render a loading skeleton state. */
  loading?: boolean;
  /** Message shown when there are no entries. */
  emptyMessage?: string;
  className?: string;
}

/**
 * Artifact timeline (spec §13 / contract §8): merged events, rule runs, agent
 * runs, and decisions rendered as a vertical, chronological feed.
 */
export function Timeline({
  items,
  loading = false,
  emptyMessage = "No timeline activity yet.",
  className,
}: TimelineProps) {
  if (loading) {
    return (
      <div className={cn("space-y-3", className)}>
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="mt-1.5 h-2 w-2 shrink-0 animate-pulse rounded-full bg-muted" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
              <div className="h-3 w-1/4 animate-pulse rounded bg-muted" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <p className={cn("text-sm text-muted-foreground", className)}>
        {emptyMessage}
      </p>
    );
  }

  // §5.5: a left rail (1px hairline) with status-coloured dots; each entry is a
  // row separated by whitespace — no card per entry, the dot carries the colour.
  return (
    <ol className={cn("relative space-y-3 pl-4", className)}>
      <span
        className="absolute bottom-2 left-[3px] top-2 w-px bg-border"
        aria-hidden="true"
      />
      {items.map((entry) => (
        <li
          key={`${entry.kind}-${entry.id}`}
          className="relative flex gap-3"
        >
          <StatusDot
            status={entry.status}
            tone={entry.status ? undefined : "neutral"}
            className="absolute -left-4 top-1.5 ring-4 ring-background"
          />
          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {KIND_LABEL[entry.kind]}
              </span>
              {entry.status ? <StatusBadge status={entry.status} /> : null}
            </div>
            <p className="mt-0.5 text-sm font-medium text-foreground">
              {entry.title}
            </p>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground tabular-nums">
              <time dateTime={entry.at} title={formatAt(entry.at)}>
                {formatAt(entry.at)}
              </time>
              {entry.ref?.event_type ? (
                <span>event: {entry.ref.event_type}</span>
              ) : null}
              {entry.ref?.rule_id ? (
                <span>rule: {entry.ref.rule_id}</span>
              ) : null}
              {entry.ref?.executor_type ? (
                <span>executor: {entry.ref.executor_type}</span>
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default Timeline;
